/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import style from './editing.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUser, updateAvatar, updateSelf } from 'store/auth/authSlice';
import { getErrorsProps } from 'utils/utils';
import Button from 'controls/buttons/submit';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl } from '@mui/material';
import FormTextField from 'controls/textField/formTextField';
import cx from 'classnames';
import AvatarUpload, { SendRef } from 'components/avatarUpload/avatarUpload';
import size from 'lodash-es/size';
import { FilePondFile } from 'filepond';
import UploadModal from 'components/modal/uploadModal';
import { selectAvatarFile, setAvatarFile } from 'store/transitionSlice';
import isEmpty from 'lodash-es/isEmpty';
import keys from 'lodash-es/keys';
import { deleteAvatar } from 'api/Api';
import { MIN_TABLET_WIDTH } from 'utils/utils';
import { useViewport } from 'contexts/viewportContext';

export type ProfileMode = 'static' | 'editing';

export default function UserCardEditing({
  onChangeMode,
}: {
  onChangeMode: React.Dispatch<React.SetStateAction<ProfileMode>>;
}) {
  const { width } = useViewport();
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isWasSubmitted, setIsWasSubmitted] = useState(false);
  const avatarRef = useRef<SendRef>(null);
  const [files, setFiles] = useState<FilePondFile[]>([]);
  const [isDeleted, setIsDeleted] = useState(false);
  const [currentModal, setCurrentModal] = useState<null | React.ReactElement>(
    null
  );
  const avatarFile = useAppSelector(selectAvatarFile);
  //   const isMobileAdaptiveModeOn = window.innerWidth < MIN_TABLET_WIDTH;
  const isMobileAdaptiveModeOn = width <= MIN_TABLET_WIDTH;

  useEffect(() => {
    return () => {
      dispatch(setAvatarFile({}));
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(avatarFile) && avatarRef?.current) {
      avatarRef.current.setFile(avatarFile[keys(avatarFile)[0]].file);
    }
  }, [avatarFile]);

  const schema = yup.object({
    name: yup.string().required('Поле не может быть пустым'),
    surname: yup.string().required('Поле не может быть пустым'),
    organization: yup.string().required('Поле не может быть пустым'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: user?.name || '',
      surname: user?.surname || '',
      organization: user?.organization || '',
    },
  });

  useEffect(() => {
    const isAvatarChanged = size(files) > 0 || (size(files) === 0 && isDeleted);
    const isFieldsChanged = methods.formState.isDirty;

    setIsDisabled(!(isFieldsChanged || isAvatarChanged));
  }, [methods.formState.isDirty, files, isDeleted]);

  useEffect(() => {
    if (isWasSubmitted && size(files) === 0) {
      onChangeMode('static');
    }
  }, [isWasSubmitted, size(files)]);

  const onSubmit = async (data: any) => {
    if (size(files) > 0 && avatarRef?.current && user) {
      avatarRef.current.send();
    } else if (size(files) === 0 && isDeleted && user?._id) {
      setIsLoading(true);
      await dispatch(
        updateAvatar(
          deleteAvatar({
            _id: user._id,
          })
        )
      );
    }

    if (user && methods.formState.isDirty) {
      setIsLoading(true);
      await dispatch(
        updateSelf({
          _id: user._id,
          name: data.name,
          surname: data.surname,
          organization: data.organization,
        })
      );
    }

    setIsWasSubmitted(true);
    setIsLoading(false);
    setIsDeleted(false);
  };

  const adaptedErrors = getErrorsProps(methods.formState.errors);
  return (
    <FormControl className={style.container}>
      {currentModal !== null && currentModal}
      <FormProvider {...methods}>
        <form
          ref={formRef}
          className={cx(style.form, 'adaptive-editing__form')}
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <div className={style.left}>
            <span className={'adaptive-f20'}>Изображение профиля</span>
            <div
              className={cx(
                style.avatarContainer,
                'adaptive-editing__avatarContainer'
              )}
            >
              <AvatarUpload
                ref={avatarRef}
                className={cx(style.avatar, 'adaptive-editing__avatar')}
                onProcessStart={() => setIsLoading(true)}
                onProcessEnd={() => setIsLoading(false)}
                onUpdatefiles={setFiles}
                onDelete={() => {
                  setIsDeleted(true);
                }}
                isDeleted={isDeleted}
              ></AvatarUpload>
              <span
                className={cx(style.change, 'clickable-text', 'adaptive-f18')}
                onClick={() => {
                  dispatch(setAvatarFile({}));

                  setCurrentModal(
                    <UploadModal
                      onClose={() => {
                        setCurrentModal(null);
                      }}
                      setter={setAvatarFile}
                      isMultiple={false}
                    ></UploadModal>
                  );
                }}
              >{`${user?.avatar ? 'Изменить' : 'Загрузить'} изображение`}</span>
            </div>

            {!isMobileAdaptiveModeOn && (
              <div
                className={cx(
                  style.buttons,
                  'buttons-group',
                  'adaptive-editing__buttons'
                )}
              >
                <Button
                  onClick={() => {}}
                  disabled={isDisabled}
                  wrapper={{
                    className: 'adaptive-editing__submit_container',
                    loading: isLoading,
                  }}
                >
                  {!isLoading && 'Сохранить'}
                </Button>
                <Button
                  wrapper={{
                    className: 'adaptive-editing__submit_container',
                  }}
                  className={'btn-cancel'}
                  onClick={() => onChangeMode('static')}
                >
                  Назад
                </Button>
              </div>
            )}
          </div>

          <div className={style.right}>
            <span className={'adaptive-f20'}>Общая информация</span>
            <div className={cx(style.fields, 'adaptive-editing__fields')}>
              <FormTextField
                name="name"
                inner_props={{
                  label: 'Имя',
                  InputProps: {
                    placeholder: 'Имя',
                  },
                  ...adaptedErrors['name'],
                }}
                defaultValue={user?.name}
              ></FormTextField>
              <FormTextField
                name="surname"
                inner_props={{
                  label: 'Фамилия',
                  InputProps: {
                    placeholder: 'Фамилия',
                  },
                  ...adaptedErrors['surname'],
                }}
                defaultValue={user?.surname}
              ></FormTextField>
              <FormTextField
                name="organization"
                inner_props={{
                  label: 'Организация',
                  InputProps: {
                    placeholder: 'Организация',
                  },
                  ...adaptedErrors['organization'],
                }}
                defaultValue={user?.organization}
              ></FormTextField>
              {isMobileAdaptiveModeOn && (
                <div
                  className={cx(
                    style.buttons,
                    'buttons-group',
                    'adaptive-editing__buttons'
                  )}
                >
                  <Button
                    onClick={() => {}}
                    disabled={isDisabled}
                    wrapper={{
                      className: 'adaptive-editing__submit_container',
                      loading: isLoading,
                    }}
                  >
                    {!isLoading && 'Сохранить'}
                  </Button>
                  <Button
                    wrapper={{
                      className: 'adaptive-editing__submit_container',
                    }}
                    className={'btn-cancel'}
                    onClick={() => onChangeMode('static')}
                  >
                    Назад
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </FormControl>
  );
}
